// src/dashboard/pages/teacher/Categories/hooks/useCategoriesDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllAdherentsListWithGroup,
  updatePoids,
} from "../../../../../services/adherentServices";

import {
  buildCategoryRowsForDisplay,
  buildCountsByBracket,
  computeCategoryStats,
  computeProcessedMembers,
  filterMembers,
  getAgeCategoryOptions,
  getGroupOptions,
  getSeasonOptions,
  getYearOptions,
  sortMembers,
} from "../utils/categoriesDashboardUtils";

const defaultFilters = {
  viewMode: "distribution",
  season: "",
  genre: "Tous",
  year: "Tous",
  group: "",
  ageCategory: "Tous",
  query: "",
  sortBy: "name-asc",

  // Nouveau : par défaut on affiche seulement les adhérents avec poids.
  weightStatus: "with", // "with" | "without" | "all"

  onlyFilledCategories: true,
};

export function useCategoriesDashboard() {
  const [adherents, setAdherents] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [loading, setLoading] = useState(true);
  const [updatingWeightId, setUpdatingWeightId] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState("");
  const [drawerMembers, setDrawerMembers] = useState([]);

  const seasonOptions = useMemo(() => getSeasonOptions(), []);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      season: prev.season || seasonOptions[1] || seasonOptions[0] || "",
    }));
  }, [seasonOptions]);

  const fetchAdherents = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getAllAdherentsListWithGroup();
      setAdherents(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Erreur chargement catégories :", error);
      toast.error("Erreur lors du chargement des adhérents.");
      setAdherents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdherents();
  }, [fetchAdherents]);

  const groupOptions = useMemo(() => {
    return getGroupOptions(adherents);
  }, [adherents]);

  const yearOptions = useMemo(() => {
    return getYearOptions();
  }, []);

  const ageCategoryOptions = useMemo(() => {
    return getAgeCategoryOptions(filters.season, filters.genre);
  }, [filters.season, filters.genre]);

  useEffect(() => {
    const exists = ageCategoryOptions.some(
      (item) => item.value === filters.ageCategory
    );

    if (filters.ageCategory !== "Tous" && !exists) {
      setFilters((prev) => ({
        ...prev,
        ageCategory: "Tous",
      }));
    }
  }, [ageCategoryOptions, filters.ageCategory]);

  const { processedMembers, excludedMembers, allDisplayMembers } = useMemo(() => {
    return computeProcessedMembers(adherents, filters.season, filters.group);
  }, [adherents, filters.season, filters.group]);

  const filteredMembers = useMemo(() => {
    const filtered = filterMembers(allDisplayMembers, filters);
    return sortMembers(filtered, filters.sortBy);
  }, [allDisplayMembers, filters]);

  const categorizedFilteredMembers = useMemo(() => {
    return filteredMembers.filter((member) => member.isCategorized);
  }, [filteredMembers]);

  const { counts, membersByBracket } = useMemo(() => {
    return buildCountsByBracket(categorizedFilteredMembers);
  }, [categorizedFilteredMembers]);

  const distributionRows = useMemo(() => {
    return buildCategoryRowsForDisplay({
      season: filters.season,
      genre: filters.genre,
      year: filters.year,
      ageCategory: filters.ageCategory,
      onlyFilledCategories: filters.onlyFilledCategories,
      counts,
    });
  }, [
    filters.season,
    filters.genre,
    filters.year,
    filters.ageCategory,
    filters.onlyFilledCategories,
    counts,
  ]);

  const stats = useMemo(() => {
    return computeCategoryStats({
      adherents,
      processedMembers,
      filteredMembers,
      excludedMembers,
      counts,
    });
  }, [adherents, processedMembers, filteredMembers, excludedMembers, counts]);

  const openMembersDrawer = useCallback(
    ({ row, bracket }) => {
      const key = `${row.ageCategory}|${row.genre}|${bracket}`;
      const list = membersByBracket[key] || [];

      setDrawerTitle(`${row.label} — ${row.genre} — ${bracket}`);
      setDrawerMembers(sortMembers(list, "name-asc"));
      setDrawerOpen(true);
    },
    [membersByBracket]
  );

  const closeMembersDrawer = () => {
    setDrawerOpen(false);
    setDrawerTitle("");
    setDrawerMembers([]);
  };

  const handleUpdateWeight = useCallback(async (member, newWeight) => {
    if (!member?.id) {
      toast.error("Adhérent introuvable.");
      return;
    }

    const normalized = Number(String(newWeight).replace(",", "."));

    if (!Number.isFinite(normalized) || normalized <= 0) {
      toast.warn("Veuillez saisir un poids valide.");
      return;
    }

    try {
      setUpdatingWeightId(member.id);

      await updatePoids(member.id, normalized);

      setAdherents((prev) =>
        prev.map((adherent) =>
          adherent.id === member.id
            ? {
                ...adherent,
                poids: normalized,
              }
            : adherent
        )
      );

      toast.success("Poids mis à jour.");
    } catch (error) {
      console.error("Erreur mise à jour poids :", error);
      toast.error("Erreur lors de la mise à jour du poids.");
    } finally {
      setUpdatingWeightId(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchAdherents();
  }, [fetchAdherents]);

  return {
    filters,
    setFilters,

    loading,
    updatingWeightId,

    adherents,
    processedMembers,
    filteredMembers,
    excludedMembers,

    groupOptions,
    seasonOptions,
    yearOptions,
    ageCategoryOptions,

    stats,
    distributionRows,
    counts,
    membersByBracket,

    drawerOpen,
    drawerTitle,
    drawerMembers,
    openMembersDrawer,
    closeMembersDrawer,

    handleUpdateWeight,
    refresh,
  };
}